import {
	Component,
	OnInit,
	OnDestroy,
	AfterViewInit,
	Output,
	EventEmitter,
	ChangeDetectionStrategy,
	ViewChild,
	Input,
	HostListener,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { takeUntil, take } from 'rxjs/operators';
import { firstValueFrom, fromEvent } from 'rxjs';

import {
	DELAY_CONTACT_LABELS,
	TO_DOLLARS,
	ProjectGroup,
	ProjectCarrierMapDTO,
	IS_NOT_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';

import { BaseComponent } from '@shared/base/base.component';

import { ProcuredCarriersComponent } from '../procured-carriers/procured-carriers.component';
import { ProjectDetailsStore } from '../store/store/project-details.store';
import { RecommendedCarriersStore } from '../store/store/recommended-carriers-store';
import { PROJECT_GROUPS } from '../store/actions/';

import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { projectDetailsPageStateKey } from '../store/state';

@Component({
	selector: 'app-project-groups',
	templateUrl: './project-groups.component.html',
	styleUrls: ['./project-groups.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectGroupsComponent
	extends BaseComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	@Output() procuredCarriersSelectionUpdated: EventEmitter<any> = new EventEmitter();
	@Output() openFullProfileEvent: EventEmitter<any> = new EventEmitter();

	@ViewChild('groupCarriersComponent') procuredCarriersComponent: ProcuredCarriersComponent;

	DELAY_CONTACT_LABELS = DELAY_CONTACT_LABELS;
	TO_DOLLARS = TO_DOLLARS;
	selectedProjectGroupId!: string;
	selectedProjectGroupIds: Array<string> = [];
	selectedProjectCarrierMapIds: Array<string> = [];
	ungroupedSelectedProjectCarrierMapIds: Array<string> = [];
	selectedCarrierIds: Array<string> = [];
	highestPriorityGroupSelected: boolean = false;
	lowestPriorityGroupSelected: boolean = false;
	selectedGroup!: ProjectGroup;
	systemGroupSelected: boolean = false;
	createGroupFromUserGroupEnabled: boolean = false;

	readonly TAB_GROUP_HEADER_HEIGHT = 48;
	readonly COUNT_STAT_ROW = 18;
	readonly GROUP_CARD = 56;
	readonly GROUP_PADDING = 12;
	readonly DEFAULT_CONTAINER_HEIGHT = 400;

	constructor(
		public readonly projectDetailsStore: ProjectDetailsStore,
		public readonly recommendedCarriersStore: RecommendedCarriersStore,
		private store: Store,
		private actions$: Actions<any>,
		public accountSettings: AccountSettingsService
	) {
		super();
	}

	ngOnInit() {}

	ngAfterViewInit() {
		this.listenToScrollEvents();
	}

	expand(projectGroup: ProjectGroup) {
		this.selectedGroup = projectGroup;
		this.selectedProjectGroupId = projectGroup.id;
	}

	collapse(projectGroup: ProjectGroup) {
		this.selectedGroup = null;
		this.selectedProjectGroupId = null;
		this.emptySelectedData();
	}

	emptySelectedData() {
		this.selectedProjectCarrierMapIds = [].concat([]);
		this.selectedCarrierIds = [].concat([]);
		this.systemGroupSelected = false;
		this.createGroupFromUserGroupEnabled = false;
		this.procuredCarriersSelectionUpdated.emit(this.selectedCarrierIds);
	}

	onProjectGroupCardClick(projectGroup: ProjectGroup, event: MouseEvent) {
		if (!event.shiftKey && (!this.selectedGroup || this.selectedGroup.id !== projectGroup.id)) {
			this.emptySelectedData();
			this.expand(projectGroup);
		} else {
			this.collapse(projectGroup);
		}
		document.getSelection().removeAllRanges();
		if (!event.shiftKey) {
			if (this.selectedProjectGroupIds.length > 1) {
				this.selectedProjectGroupIds.length = 0;
			}
			if (this.selectedProjectGroupIds.indexOf(projectGroup.id) > -1) {
				this.selectedProjectGroupIds.length = 0;
			} else {
				this.selectedProjectGroupIds.length = 0;
				this.selectedProjectGroupIds.push(projectGroup.id);
			}
		} else {
			if (this.selectedProjectGroupIds.indexOf(projectGroup.id) > -1) {
				const matchedIndex = this.selectedProjectGroupIds.indexOf(projectGroup.id);
				this.selectedProjectGroupIds.splice(matchedIndex, 1);
			} else {
				this.selectedProjectGroupIds.push(projectGroup.id);
			}
		}
		this.highestPriorityGroupSelected = false;
		this.lowestPriorityGroupSelected = false;
		this.systemGroupSelected = false;
		if (this.selectedProjectGroupIds.length) {
			this.store
				.select((state) => state[projectDetailsPageStateKey])
				.pipe(takeUntil(this.isDestroyed$), take(1))
				.subscribe((state) => {
					const projectGroups = state.projectGroups,
						systemGroup = state.projectGroups.find((group) => group.systemGroup);
					// if only one user group select highest and lowest groups
					const userGroups = state.projectGroups.filter((group) => !group.systemGroup);
					if (userGroups.length === 1) {
						this.lowestPriorityGroupSelected = true;
						this.highestPriorityGroupSelected = true;
					} else {
						projectGroups.forEach((group, index) => {
							if (index === 0 && this.selectedProjectGroupIds.indexOf(group.id) !== -1) {
								this.highestPriorityGroupSelected = true;
							} else if (
								!systemGroup &&
								index === projectGroups.length - 1 &&
								this.selectedProjectGroupIds.indexOf(group.id) !== -1
							) {
								this.lowestPriorityGroupSelected = true;
							} else if (
								systemGroup &&
								index === projectGroups.length - 2 &&
								this.selectedProjectGroupIds.indexOf(group.id) !== -1
							) {
								this.lowestPriorityGroupSelected = true;
							}
						});
					}
					this.systemGroupSelected = IS_NOT_NULL_OR_UNDEFINED(
						state.projectGroups.find(
							(group) => group.systemGroup && this.selectedProjectGroupIds.indexOf(group.id) !== -1
						)
					);
				});
		}
	}

	onProcuredCarrierClick(projectCarrierMap: ProjectCarrierMapDTO, event: MouseEvent) {
		document.getSelection().removeAllRanges();
		if (!event.shiftKey) {
			if (this.selectedProjectCarrierMapIds.length > 1) {
				this.selectedProjectCarrierMapIds = [];
				this.selectedCarrierIds = [];
			}
			if (this.selectedProjectCarrierMapIds.indexOf(projectCarrierMap.id) > -1) {
				this.selectedProjectCarrierMapIds = [];
				this.selectedCarrierIds = [];
			} else {
				this.selectedProjectCarrierMapIds = [];
				this.selectedCarrierIds = [];
				this.selectedProjectCarrierMapIds.push(projectCarrierMap.id);
				this.selectedCarrierIds.push(projectCarrierMap.carrier.id);
			}
		} else {
			if (this.selectedProjectCarrierMapIds.indexOf(projectCarrierMap.id) > -1) {
				const matchedIndex = this.selectedProjectCarrierMapIds.indexOf(projectCarrierMap.id);
				this.selectedProjectCarrierMapIds.splice(matchedIndex, 1);
				this.selectedCarrierIds = this.selectedCarrierIds.filter(
					(carrierId) => carrierId !== projectCarrierMap.carrier.id
				);
			} else {
				this.selectedProjectCarrierMapIds.push(projectCarrierMap.id);
				this.selectedCarrierIds.push(projectCarrierMap.carrier.id);
			}
		}
		this.ungroupedSelectedProjectCarrierMapIds = [];
		if (this.selectedGroup?.systemGroup) {
			this.ungroupedSelectedProjectCarrierMapIds = [].concat(this.selectedProjectCarrierMapIds);
		}
		this.procuredCarriersSelectionUpdated.emit(this.selectedCarrierIds);

		this.createGroupFromUserGroupEnabled = false;
		if (
			this.selectedProjectCarrierMapIds.length &&
			!this.systemGroupSelected &&
			this.selectedProjectGroupIds.length === 1
		) {
			this.store
				.select((state) => state[projectDetailsPageStateKey])
				.pipe(takeUntil(this.isDestroyed$), take(1))
				.subscribe((state) => {
					// check if user can create a new group from existing user group
					const userGroup = state.projectGroups.find(
						(group) => group.id === projectCarrierMap.projectGroup.id
					);
					if (userGroup) {
						const pcmIds = userGroup.projectCarrierMapsList.map((pcm) => pcm.id);
						const allCarriersInGroupSelected = pcmIds.every((pcmId) =>
							this.selectedProjectCarrierMapIds.includes(pcmId)
						);
						if (!allCarriersInGroupSelected) {
							this.createGroupFromUserGroupEnabled = true;
						}
					}
				});
		}
	}

	getChildViewportHeight(): string {
		const containerHeight: number =
			this.getContainerWrapperHeight() -
			(this.TAB_GROUP_HEADER_HEIGHT + this.COUNT_STAT_ROW + this.GROUP_CARD + this.GROUP_PADDING);
		return `${containerHeight}px`;
	}

	getContainerHeight(): string {
		const containerHeight: number =
			this.getContainerWrapperHeight() - (this.TAB_GROUP_HEADER_HEIGHT + this.COUNT_STAT_ROW);
		return `${containerHeight}px`;
	}

	getContainerWrapperHeight(): number {
		const element = document.getElementById('projectDetailsContent');
		if (!element) {
			return this.DEFAULT_CONTAINER_HEIGHT;
		}

		return element.offsetHeight;
	}

	listenToScrollEvents() {
		fromEvent(window, 'scroll')
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((event: any) => {
				this.onScroll(event);
			});
	}

	onScroll(event: WheelEvent) {
		const parentContainer = document.querySelector('.parent-container');
		const childViewport = document.querySelector('.child-viewport');

		if (childViewport && parentContainer) {
			const isChildAtTop = childViewport.scrollTop === 0;
			const isChildAtBottom =
				childViewport.scrollTop + childViewport.clientHeight >= childViewport.scrollHeight;

			if (isChildAtTop && event.deltaY < 0) {
				this.scrollToPreviousParent();
				event.preventDefault();
			} else if (isChildAtBottom && event.deltaY > 0) {
				this.scrollToNextParent();
				event.preventDefault();
			} else {
				return;
			}
		}
	}

	async scrollToPreviousParent() {
		const parentCards = document.querySelectorAll('.parent-card');
		let targetParent: HTMLElement | null = null;
		const groupsList = await firstValueFrom(this.projectDetailsStore.projectGroups$);

		parentCards.forEach((parent, index) => {
			const childViewport = parent.querySelector('.child-viewport');
			if (childViewport && childViewport.scrollTop === 0) {
				for (let i = index - 1; i >= 0; i--) {
					if (groupsList[i].id != this.selectedProjectGroupId) {
						targetParent = parentCards[i] as HTMLElement;
						break;
					}
				}
			}
		});

		if (targetParent) {
			const offset = 20;
			const targetPosition = targetParent.offsetTop - offset;
			document.querySelector('.parent-container')!.scrollTo({
				top: targetPosition,
				behavior: 'smooth',
			});
		}
	}

	scrollToNextParent() {
		const parentCards = document.querySelectorAll('.parent-card');
		let targetParent: HTMLElement | null = null;

		parentCards.forEach((parent, index) => {
			const childViewport = parent.querySelector('.child-viewport');
			if (
				childViewport &&
				childViewport.scrollTop + childViewport.clientHeight >= childViewport.scrollHeight
			) {
				if (index < parentCards.length - 1) {
					targetParent = parentCards[index + 1] as HTMLElement;
				} else {
					const parentContainer = document.querySelector('.parent-container');
					if (parentContainer) {
						parentContainer.scrollTo({
							top: parentContainer.scrollHeight,
							behavior: 'smooth',
						});
					}
				}
			}
		});

		if (targetParent) {
			const offset = 20;
			const targetPosition = targetParent.offsetTop - offset;
			document.querySelector('.parent-container')!.scrollTo({
				top: targetPosition,
				behavior: 'smooth',
			});
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
